// ALL IMPORTS MUST BE AT THE TOP - NO CODE BETWEEN THEM!
import React, { useMemo, useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Breadcrumb from '../components/Breadcrumb';
import { Link } from 'react-router-dom';
import AspireCTA from '../components/AspireCTAComponent.js';
import ResidentialComponent from '../components/ResidentialComponent.js';
import AspireSection from '../components/AspireSection.js';
import EcoFriendlySection from '../components/EcoFriendlySection.js';
import '../styles/clientPage.css';
import CTASection from '../components/CTASection';
import { residentialServices } from '../data/residentialServicesData.js';

if (typeof AspireCTA !== 'function') {
  console.error("❌ AspireCTA is NOT a function! It's:", AspireCTA);
  console.error("❌ AspireCTA type:", typeof AspireCTA);
  console.error("❌ AspireCTA keys:", Object.keys(AspireCTA || {}));
}

const loadResidentialData = () => import('../data/residentialServicesData.js');

const LoadingSpinner = ({ text = "Loading..." }) => (
  <div className="loading-spinner">{text}</div>
);

const ResidentialClientPage = () => {
  const [residentialData, setResidentialData] = useState(null);
  // State for expandable services
  const [expandedServices, setExpandedServices] = useState({});

  useEffect(() => {
    loadResidentialData().then(data => setResidentialData(data));
  }, []);

  const toggleService = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const staticContent = useMemo(() => ({
    heroStats: [
      { number: "35", label: "Years of Excellence" },
      { number: "365", label: "Days of Care" }
    ]
  }), []);

  return (
    <main className="client-page">
      <Navbar/>
      <Breadcrumb />

      {/* Hero Section */}
      <section className="residential-hero">
        <img 
          src="/images/maintenance/resHero.JPG" 
          alt="Residential Landscaping Services" 
          className="hero-image"
          loading="eager"
          decoding="async"
        />
        <div className="residential-hero-content">
          <h1 className="residential-hero-title">Elegant, Low-Maintenance Landscapes</h1>
          <p className="hero-subtitle">
            Designed and maintained with care, so you can enjoy more and manage less
          </p>
          <div className="hero-buttons">
            <div className="header-aspire-cta">
              <AspireCTA 
                variant="standard"
                size="large"
                text="Aspire Platform"
                subtext="View updates, schedules & photos"
                icon="📊"
              />
            </div>
            <Link to="/gallery" className="btn-primary">Gallery</Link>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <CompanyIntroSection companyInfo={residentialData?.companyInfo} />

      {/* Aspire Section */}
      <AspireSection 
        showLogos={false}
        layout="split"
        quoteHighlight={true}
        showCTA={true}
        ctaText="Access Your Project Dashboard"
        ctaLink="/client-portal"
      />

      {/* Residential Services */}
      <section className="residential-showcase">
        <div className="section-inner">
          <div className="showcase-header">
            <h3>RESIDENTIAL EXCELLENCE</h3>
            <div className="header-line"></div>
          </div>
          <div className="showcase-grid">
            <div className="showcase-image showcase-image--main">
              <img src="/images/landscape/frontLawn.png" alt="Professional landscaping" />
              <div className="image-label">
                <span className="label-number">01</span>
                <span className="label-text">LANDSCAPE DESIGN</span>
              </div>
            </div>
            <div className="showcase-image showcase-image--secondary">
              <img src="/images/maintenance/HEROmaintenance1.jpeg" alt="Maintenance services" />
              <div className="image-label">
                <span className="label-number">02</span>
                <span className="label-text">MAINTENANCE PROGRAMS</span>
              </div>
            </div>
            <div className="showcase-image showcase-image--accent">
              <img src="/images/water/estateIrrigation.jpg" alt="Irrigation systems" />
              <div className="image-label">
                <span className="label-number">03</span>
                <span className="label-text">IRRIGATION SYSTEMS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="residential-services">
        <div className="section-inner">
          <h2 className="section-title text-center">Complete Residential Services</h2>
          <p className="section-intro text-center">
            Comprehensive landscaping solutions designed for your lifestyle
          </p>
          <div className="services-compact-grid">
            {residentialServices.map((service) => (
              <div key={service.id} className="service-compact-card">
                <div className="service-preview">
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                  </div>
                  <h3>{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
                
                <button 
                  className="expand-services-btn" 
                  onClick={() => toggleService(service.id)}
                >
                  {expandedServices[service.id] ? 'View Less' : 'View More'}
                </button>
                
                <div className={`services-detailed ${expandedServices[service.id] ? 'expanded' : ''}`}>
                  <div className="service-features">
                    <h4>What We Provide:</h4>
                    <ul>
                      {service.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="aspire-benefits">
                    <h4>Your Digital Experience:</h4>
                    <ul className="aspire-list">
                      {service.aspireFeatures.map((feature, index) => (
                        <li key={index}>
                          <span className="aspire-icon">⚡</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eco-Friendly Practices Section */}
      <EcoFriendlySection 
        layout="compact"
        showTitle={true}
        showCTA={true}
        variant="light"
        context="residential"
      />

      {/* Customer Benefits */}
      {/* <CustomerBenefitsSection customerBenefits={residentialData?.customerBenefits} /> */}

      {/* Final CTA */}
       <CTASection 
  title="Leave the work to us"
  subtitle="Enjoy the outdoor space without the hassle. We value attention to detail, consistent service and beautiful landscaping that enhances homes and year-round lifestyle."
  galleryLink="/Gallery"
  showEmergency={false}
/>
    </main>
  );
};

const CompanyIntroSection = ({ companyInfo }) => {
  if (!companyInfo) return <LoadingSpinner />;

  return (
    // <section className="company-intro">
    //   <div className="intro-container">
    //     <div className="intro-content">
    //       <h2>Beautiful Landscaping that Fits Your Lifestyle</h2>
    //       <p className="intro-text">{companyInfo.philosophy}</p>
    //       <div className="intro-specialties">
    //         {companyInfo.specialties.map((specialty, index) => (
    //           <div key={index} className="specialty-item">
    //             <span className="specialty-check">✓</span>
    //             <span>{specialty}</span>
    //           </div>
    //         ))}
    //       </div>
    //     </div>
    //   </div>
    // </section>
  <ResidentialComponent 
  variant="editorial"  // ← This is key!
  showNumbers={true}   // For the 01, 02, 03 numbering
  images={[            // Optional images
    {src: '/images/landscape/frontLawn.png', alt: 'Image 1'},
    {src: '/images/landscape/frontLawn.png', alt: 'Image 2'},
    {src: '/images/landscape/frontLawn.png', alt: 'Image 3'}
  ]}
/>
  );
};

// Simplified editorial showcase - no complex data loading needed

// const CustomerBenefitsSection = ({ customerBenefits }) => {
//   if (!customerBenefits) return <LoadingSpinner text="Loading benefits..." />;

//   return (
//     <section className="customer-benefits-section">
//       <div className="benefits-container">
//         <div className="benefits-header">
//           <h2>Why Homeowners Choose All Seasons</h2>
//           <p>Elegant results, dependable service, and the peace of mind of professional technology and support.</p>
//         </div>

//         <div className="benefits-grid">
//           {customerBenefits.map((benefit, index) => (
//             <BenefitCard key={index} benefit={benefit} />
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

const ServiceCard = React.memo(({ service }) => (
  <div className="service-card">
    <div className="service-card-header">
      <div className="service-icon">{service.icon}</div>
      <h3>{service.title}</h3>
    </div>

    <p className="service-description">{service.description}</p>

    <div className="service-features">
      <h4>What We Provide:</h4>
      <ul>
        {service.features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
    </div>

    <div className="aspire-benefits">
      <h4>Your Digital Experience:</h4>
      <ul className="aspire-list">
        {service.aspireFeatures.map((feature, index) => (
          <li key={index}>
            <span className="aspire-icon">⚡</span>
            {feature}
          </li>
        ))}
      </ul>
    </div>

    <div className="service-cta">
      <a href={`/services/${service.id}`} className="service-link">
        Learn More <span className="link-arrow">→</span>
      </a>
    </div>
  </div>
));

const BenefitCard = React.memo(({ benefit }) => (
  <div className="benefit-card">
    <div className="benefit-icon">{benefit.icon}</div>
    <h3>{benefit.title}</h3>
    <p>{benefit.description}</p>
  </div>
));

export default ResidentialClientPage;
